import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtPct } from "../lib/format";

const HEALTH_COLOR = (h) => h >= 85 ? "#4af6c3" : h >= 70 ? "#fb8b1e" : "#ff433d";
const RISK_COLOR = { "Low": "#4af6c3", "Medium": "#fb8b1e", "High": "#ff433d" };

export default function Projects() {
    const [data, setData] = useState(null);
    const [risk, setRisk] = useState("All");
    useEffect(() => { api.get("/projects").then((r) => setData(r.data)); }, []);
    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING PROJECTS...</div>;

    const filtered = data.projects.filter((p) => risk === "All" || p.risk === risk);

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// PROJECT PORTFOLIO</div>
                <h1 className="font-display font-black text-3xl">All Sites, One View</h1>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <KpiCard testId="proj-total" label="Total Contract" value={data.summary.total_contract} sub={`${data.projects.length} active`} accent />
                <KpiCard testId="proj-billed" label="Billed to Date" value={data.summary.total_billed} />
                <KpiCard testId="proj-collected" label="Collected" value={data.summary.total_collected} />
                <KpiCard testId="proj-avg-completion" label="Avg Completion" value={`${data.summary.avg_completion.toFixed(0)}%`} format="custom" sub="Weighted" />
                <KpiCard testId="proj-at-risk" label="At Risk" value={data.summary.at_risk_count} format="custom" sub="High-risk projects" />
            </div>

            <div className="flex items-center gap-2 font-mono-fin text-[10px] uppercase tracking-wider">
                <span className="text-[var(--text-secondary)]">Filter:</span>
                {["All", "Low", "Medium", "High"].map((r) => (
                    <button
                        key={r}
                        data-testid={`filter-risk-${r.toLowerCase()}`}
                        onClick={() => setRisk(r)}
                        className={`px-3 py-1 border ${risk === r ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)] text-[var(--text-primary)]" : "border-panel text-[var(--text-secondary)]"}`}
                    >
                        {r}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {filtered.map((p) => (
                    <Link
                        key={p.id}
                        to={`/projects/${p.id}`}
                        data-testid={`project-card-${p.id}`}
                        className="bg-panel border border-panel p-4 hover:border-[var(--brand-amber)] transition-colors rise-in"
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)]">{p.code}</div>
                                <div className="font-display font-bold text-[var(--text-primary)] text-sm leading-tight mt-1">{p.name}</div>
                                <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider mt-1">{p.location}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[9px] font-mono-fin uppercase tracking-wider" style={{ color: RISK_COLOR[p.risk] }}>{p.risk} Risk</div>
                                <div className="font-mono-fin text-2xl font-bold" style={{ color: HEALTH_COLOR(p.health) }}>{p.health}</div>
                                <div className="text-[9px] font-mono-fin text-[var(--text-muted)]">HEALTH</div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <div className="flex justify-between text-[10px] font-mono-fin uppercase tracking-wider mb-1">
                                <span className="text-[var(--text-secondary)]">Progress</span>
                                <span className="text-[var(--text-primary)]">{p.completion_pct.toFixed(1)}%</span>
                            </div>
                            <div className="h-1.5 bg-[var(--bg-base)] border border-panel">
                                <div className="h-full bg-[var(--brand-amber)]" style={{ width: `${p.completion_pct}%` }} />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-[10px] font-mono-fin">
                            <div>
                                <div className="text-[var(--text-secondary)] uppercase tracking-wider">Contract</div>
                                <div className="text-[var(--text-primary)]">{fmtINR(p.contract_value)}</div>
                            </div>
                            <div>
                                <div className="text-[var(--text-secondary)] uppercase tracking-wider">Billed</div>
                                <div className="text-[var(--text-primary)]">{fmtINR(p.billed)}</div>
                            </div>
                            <div>
                                <div className="text-[var(--text-secondary)] uppercase tracking-wider">GP %</div>
                                <div className="text-pos">{p.gross_profit_pct.toFixed(1)}%</div>
                            </div>
                            <div>
                                <div className="text-[var(--text-secondary)] uppercase tracking-wider">SPI / CPI</div>
                                <div className={p.spi < 0.95 || p.cpi < 0.95 ? "text-neg" : "text-pos"}>{p.spi.toFixed(2)} / {p.cpi.toFixed(2)}</div>
                            </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                            {p.status} · {p.manager}
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
